// Shared intent / segment classification used by the admin visitor intel UI.

import type { ReactNode } from "react";

export interface VisitorEventLite {
  event_type: string;
  path?: string | null;
  label?: string | null;
  created_at?: string;
}

export interface VisitorLite {
  visit_count: number;
  engagement_score: number;
  total_page_views: number;
  temperature: "hot" | "warm" | "cold";
}

// --- Intent ---------------------------------------------------------------
// HIGH:   viewed /book, clicked phone, clicked Book CTA, OR 2+ service pages
// MEDIUM: viewed case study, watched a video, viewed founder/about
// LOW:    everything else (homepage only / short session)
export type Intent = "high" | "medium" | "low";

export function classifyIntent(events: VisitorEventLite[]): {
  intent: Intent;
  reasons: string[];
} {
  const reasons: string[] = [];
  const types = new Set(events.map((e) => e.event_type));
  const servicePaths = new Set(
    events.filter((e) => e.event_type === "service_page_viewed" && e.path).map((e) => e.path as string),
  );

  if (types.has("booking_page_viewed")) reasons.push("Viewed booking page");
  if (types.has("call_link_clicked")) reasons.push("Clicked phone number");
  if (types.has("book_call_clicked")) reasons.push("Clicked Book Strategy Call");
  if (servicePaths.size >= 2) reasons.push(`Visited ${servicePaths.size} service pages`);
  if (types.has("booking_form_submitted")) reasons.push("Submitted booking form");
  if (types.has("lead_form_submitted")) reasons.push("Submitted lead form");

  if (reasons.length > 0) return { intent: "high", reasons };

  const medium: string[] = [];
  if (types.has("case_study_viewed")) medium.push("Viewed a case study");
  if (types.has("video_started") || types.has("video_completed")) medium.push("Watched a video");
  if (events.some((e) => e.path === "/about" || e.path?.includes("founder"))) medium.push("Viewed founder section");
  if (servicePaths.size === 1) medium.push("Viewed a service page");

  if (medium.length > 0) return { intent: "medium", reasons: medium };

  return { intent: "low", reasons: ["Homepage / brief session"] };
}

// --- Segments -------------------------------------------------------------
// Behavioral segmentation based on which service pages the visitor consumed.
export type Segment = "investor" | "wholesaler" | "acquisition" | "unknown";

export function classifySegment(events: VisitorEventLite[]): Segment {
  const paths = events.map((e) => (e.path || "").toLowerCase());
  const hay = paths.join(" ");
  if (/acquisition|disposition/.test(hay)) return "acquisition";
  if (/wholesal/.test(hay)) return "wholesaler";
  if (/investor|cold-calling|skip-trac|appointment-setting/.test(hay)) return "investor";
  return "unknown";
}

export const segmentLabel: Record<Segment, string> = {
  investor: "Real Estate Investor",
  wholesaler: "Wholesaler",
  acquisition: "Acquisition Team",
  unknown: "Unknown",
};

// --- Recommended action ---------------------------------------------------
export function recommendedAction(intent: Intent, v: VisitorLite, reasons: string[]): string {
  if (intent === "high" && reasons.some((r) => /Submitted/.test(r))) {
    return "Lead captured, call back within 5 minutes";
  }
  if (intent === "high") return "Contact immediately if lead captured; surface in feed";
  if (intent === "medium" && v.visit_count >= 2) return "Retarget, likely evaluating options";
  if (intent === "medium") return "Watch for return visit; warm-up content next session";
  return "No action, nurture via SEO / newsletter";
}

export const intentColor = (i: Intent) =>
  i === "high" ? "hsl(0 75% 60%)" : i === "medium" ? "hsl(35 85% 60%)" : "hsl(207 30% 55%)";
